import React, { useEffect, useMemo, useCallback, useState } from "react";
import axios from "axios";

import {
  Box,
  Paper,
  Typography,
  Stack,
  Chip,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Divider,
  CircularProgress,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination,
  Tooltip,
  Avatar,
} from "@mui/material";

import NotificationsIcon from "@mui/icons-material/Notifications";
import SmsIcon from "@mui/icons-material/Sms";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import RefreshIcon from "@mui/icons-material/Refresh";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

const API_BASE =
  process.env.REACT_APP_BACKEND_URL ||
  process.env.REACT_APP_API_URL ||
  "https://qylmox.in/api";

const getAuthToken = () =>
  localStorage.getItem("token") || localStorage.getItem("authToken");

const normalizeInboxMessage = (item = {}) => ({
  _id: item._id || item.notificationId || item.broadcastId || `${item.senderId || "msg"}-${item.createdAt || Date.now()}`,
  title: item.title || (item.senderName ? `Message from ${item.senderName}` : "New Message"),
  message: item.message || "",
  read: Boolean(item.read),
  type: item.type || "broadcast",
  createdAt: item.createdAt || new Date().toISOString(),
  source: "message",
  deliveryType: item.deliveryType || item.channel || "APP",
});

const normalizeAlarmItem = (item = {}) => ({
  _id: item._id || item.alarmId || item.id || `alarm-${item.alarmType || "unknown"}-${item.createdAt || Date.now()}`,
  title: item.title || item.deviceName || item.vehicleName || item.alarmType || "Alarm",
  message:
    item.message ||
    item.description ||
    item.notes ||
    item.address ||
    item.locationText ||
    "Alarm notification",
  read: String(item.status || "").toLowerCase() !== "active",
  type: item.alarmType || item.type || "alert",
  createdAt: item.createdAt || item.timestamp || item.updatedAt || new Date().toISOString(),
  source: "alarm",
  status: item.status || "active",
  severity: item.severity || item.priority || "medium",
  raw: item,
});

const normalizeTaskNotification = (item = {}) => ({
  _id: item._id || item.notificationId || `task-${item.createdAt || Date.now()}`,
  title: item.title || "Notification",
  message: item.message || "",
  read: Boolean(item.acknowledged || item.read),
  type: item.type || "task_assigned",
  createdAt: item.createdAt || new Date().toISOString(),
  source: "task",
  raw: item,
});

const normalizeSystemNotification = (item = {}) => ({
  _id: item._id || item.notificationId || `system-${item.createdAt || Date.now()}`,
  title: item.title || "Notification",
  message: item.message || item.body || "",
  read: Boolean(item.read),
  type: item.eventType || item.type || "info",
  createdAt: item.createdAt || new Date().toISOString(),
  source: "system",
  raw: item,
});

// filter => all | unread | alert
const FILTER_TABS = [
  { value: "all", label: "All" },
  { value: "unread", label: "Unread" },
  { value: "alert", label: "System Alerts" },
];

const PAGE_SIZE = 15;

export default function NotificationsPage() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [snack, setSnack] = useState({ open: false, severity: "success", msg: "" });
  const notify = useCallback((severity, msg) => setSnack({ open: true, severity, msg }), []);

  const [confirm, setConfirm] = useState({ open: false, id: null });

  const fetchMessages = useCallback(async () => {
    try {
      setLoading(true);
      const token = getAuthToken();
      if (!token) {
        notify("warning", "Token missing. Please login again.");
        setMessages([]);
        return;
      }

      const config = { headers: { Authorization: `Bearer ${token}` } };
      const [inboxRes, alarmsRes, taskRes, systemRes] = await Promise.all([
        axios.get(`${API_BASE}/messaging/inbox?filter=all`, config),
        axios.get(`${API_BASE}/alarms`, {
          ...config,
          params: { limit: 200 },
        }),
        axios.get(`${API_BASE}/notifications/unacknowledged`, config).catch(() => ({ data: [] })),
        axios.get(`${API_BASE}/notificationswebapp/user?limit=100`, config).catch(() => ({ data: { notifications: [] } })),
      ]);

      const inbox = Array.isArray(inboxRes?.data?.data)
        ? inboxRes.data.data.map(normalizeInboxMessage)
        : [];
      const alarmsRaw = Array.isArray(alarmsRes?.data?.alarms)
        ? alarmsRes.data.alarms
        : Array.isArray(alarmsRes?.data?.data)
          ? alarmsRes.data.data
          : [];
      const alarms = alarmsRaw.map(normalizeAlarmItem);
      const taskNotifications = Array.isArray(taskRes?.data)
        ? taskRes.data.map(normalizeTaskNotification)
        : [];
      const systemNotifications = Array.isArray(systemRes?.data?.notifications)
        ? systemRes.data.notifications.map(normalizeSystemNotification)
        : [];

      const combined = [...alarms, ...inbox, ...taskNotifications, ...systemNotifications].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );

      setMessages(combined);
    } catch (error) {
      console.error("Error fetching messages:", error);
      notify("error", error?.response?.data?.msg || "Failed to load notifications.");
      setMessages([]);
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  const markAsRead = async (id) => {
    try {
      const token = getAuthToken();
      if (!token) return notify("warning", "Token missing. Please login again.");

      const target = messages.find((m) => String(m._id) === String(id));
      if (!target) return;

      if (target.source === "alarm") {
        await axios.put(
          `${API_BASE}/alarms/${id}/acknowledge`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else if (target.source === "task") {
        await axios.put(
          `${API_BASE}/notifications/${id}/acknowledge`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else if (target.source === "system") {
        await axios.put(
          `${API_BASE}/notificationswebapp/${id}/read`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.put(
          `${API_BASE}/messaging/${id}/read`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      setMessages((prev) =>
        prev.map((m) =>
          String(m._id) === String(id)
            ? { ...m, read: true, status: m.source === "alarm" ? "acknowledged" : m.status }
            : m
        )
      );
      notify(
        "success",
        target.source === "alarm"
          ? "Alarm acknowledged"
          : target.source === "task"
            ? "Notification acknowledged"
            : target.source === "system"
              ? "Notification marked as read"
            : "Marked as read"
      );
    } catch (err) {
      console.error(err);
      notify("error", err?.response?.data?.msg || "Failed to mark as read.");
    }
  };

  const markAllAsRead = async () => {
      const unread = messages.filter((m) => !m.read);
      if (unread.length === 0) return notify("info", "No unread notifications.");

    try {
      const token = getAuthToken();
      if (!token) return notify("warning", "Token missing. Please login again.");

      await Promise.all(
        unread.map((m) => {
          if (m.source === "alarm") {
            return axios.put(
              `${API_BASE}/alarms/${m._id}/acknowledge`,
              {},
              { headers: { Authorization: `Bearer ${token}` } }
            );
          }
          if (m.source === "task") {
            return axios.put(
              `${API_BASE}/notifications/${m._id}/acknowledge`,
              {},
              { headers: { Authorization: `Bearer ${token}` } }
            );
          }
          if (m.source === "system") {
            return axios.put(
              `${API_BASE}/notificationswebapp/${m._id}/read`,
              {},
              { headers: { Authorization: `Bearer ${token}` } }
            );
          }
          return axios.put(
            `${API_BASE}/messaging/${m._id}/read`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
        })
      );

      setMessages((prev) => prev.map((m) => ({ ...m, read: true })));
      notify("success", "All notifications marked as read");
    } catch (err) {
      console.error(err);
      notify("error", "Failed to mark all as read.");
    }
  };

  const askDelete = (id) => setConfirm({ open: true, id });

  const deleteMessage = async () => {
    const id = confirm.id;
    if (!id) return;

    try {
      const token = getAuthToken();
      if (!token) return notify("warning", "Token missing. Please login again.");

      const target = messages.find((m) => String(m._id) === String(id));
      if (!target) {
        setConfirm({ open: false, id: null });
        return;
      }

      if (target.source === "alarm") {
        await axios.put(
          `${API_BASE}/alarms/${id}/acknowledge`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else if (target.source === "task") {
        await axios.put(
          `${API_BASE}/notifications/${id}/acknowledge`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else if (target.source === "system") {
        await axios.delete(
          `${API_BASE}/notificationswebapp/${id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.delete(`${API_BASE}/messaging/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }

      setMessages((prev) => prev.filter((m) => m._id !== id));
      notify(
        "success",
        target.source === "alarm"
          ? "Alarm removed from list"
          : target.source === "task"
            ? "Notification acknowledged"
            : "Notification deleted"
      );
    } catch (err) {
      console.error(err);
      notify("error", err?.response?.data?.msg || "Failed to delete notification.");
    } finally {
      setConfirm({ open: false, id: null });
    }
  };

  // client-side search (server handles filter tabs)
  const filteredMessages = useMemo(() => {
    const q = search.trim().toLowerCase();
    let filtered = [...messages];

    if (filter === "unread") {
      filtered = filtered.filter((msg) => !msg.read);
    } else if (filter === "alert") {
      filtered = filtered.filter((msg) => msg.source === "alarm" || msg.type === "alert");
    }

    if (!q) return filtered;

    return filtered.filter((msg) => {
      const body = (msg.message || "").toLowerCase();
      const title = (msg.title || "").toLowerCase();
      const type = (msg.type || "").toLowerCase();
      const source = (msg.source || "").toLowerCase();
      return body.includes(q) || title.includes(q) || type.includes(q) || source.includes(q);
    });
  }, [messages, search, filter]);

  const totalPagesResolved = useMemo(
    () => Math.max(1, Math.ceil(filteredMessages.length / PAGE_SIZE)),
    [filteredMessages.length]
  );

  useEffect(() => {
    setTotalPages(totalPagesResolved);
  }, [totalPagesResolved]);

  useEffect(() => {
    setPage(1);
  }, [search, filter]);

  useEffect(() => {
    setPage((prev) => Math.min(prev, totalPagesResolved));
  }, [totalPagesResolved]);

  const pagedMessages = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredMessages.slice(start, start + PAGE_SIZE);
  }, [filteredMessages, page]);

  const unreadCount = useMemo(() => messages.filter((m) => !m.read).length, [messages]);

  const filterTabIndex = useMemo(
    () => FILTER_TABS.findIndex((t) => t.value === filter),
    [filter]
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Paper
        variant="outlined"
        sx={{
          p: { xs: 2, md: 2.5 },
          borderRadius: 3,
          mb: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Avatar sx={{ width: 44, height: 44 }}>
            <NotificationsIcon />
          </Avatar>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 900 }}>
              Notifications
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Only your own notifications are visible (role-wise / user-wise secure).
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Chip
            label={`${unreadCount} unread`}
            color={unreadCount > 0 ? "warning" : "default"}
            variant={unreadCount > 0 ? "filled" : "outlined"}
          />
          <Tooltip title="Refresh">
            <IconButton onClick={fetchMessages}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>

          <Button
            variant="outlined"
            startIcon={<MarkEmailReadIcon />}
            onClick={markAllAsRead}
            sx={{ borderRadius: 2 }}
          >
            Mark All Read
          </Button>
        </Stack>
      </Paper>

      {/* Filters + Search */}
      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden", mb: 2 }}>
        <Box sx={{ px: 2, pt: 1 }}>
          <Tabs
            value={filterTabIndex === -1 ? 0 : filterTabIndex}
            onChange={(_, idx) => {
              setPage(1);
              setFilter(FILTER_TABS[idx]?.value || "all");
            }}
            variant="scrollable"
            scrollButtons="auto"
          >
            {FILTER_TABS.map((t) => (
              <Tab key={t.value} label={t.label} />
            ))}
          </Tabs>
        </Box>

        <Divider />

        <Box sx={{ p: 2 }}>
          <TextField
            fullWidth
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            label="Search"
            placeholder="Search message / title / type..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "text.secondary" }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Paper>

      {/* Content */}
      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
        <Box sx={{ p: 2.2, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
          <Typography variant="h6" sx={{ fontWeight: 900 }}>
            Inbox
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Page {page} / {totalPages}
          </Typography>
        </Box>

        <Divider />

        <Box sx={{ p: 2.2 }}>
          {loading ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={22} />
              <Typography variant="body2" color="text.secondary">
                Loading notifications...
              </Typography>
            </Stack>
          ) : filteredMessages.length === 0 ? (
            <Alert
              icon={<ErrorOutlineIcon />}
              severity="info"
              variant="outlined"
              sx={{ borderRadius: 2.5 }}
            >
              No notifications found.
            </Alert>
          ) : (
            <Stack spacing={1.5}>
              {pagedMessages.map((msg) => {
                const isSms = msg.type === "sms" || msg.deliveryType === "SMS";
                const isAlert = msg.source === "alarm" || msg.type === "alert";
                const isTask = msg.source === "task";

                return (
                  <Paper
                    key={msg._id}
                    variant="outlined"
                    sx={{
                      p: 1.8,
                      borderRadius: 2.5,
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      gap: 1.5,
                      flexWrap: "wrap",
                      opacity: msg.read ? 0.78 : 1,
                    }}
                  >
                    <Stack direction="row" spacing={1.5} sx={{ flex: 1, minWidth: 260 }}>
                      <Avatar sx={{ width: 40, height: 40 }}>
                        {isSms ? <SmsIcon fontSize="small" /> : <NotificationsIcon fontSize="small" />}
                      </Avatar>

                      <Box sx={{ flex: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                          <Chip
                            size="small"
                            label={isAlert ? "Alarm" : isTask ? "Task" : isSms ? "SMS" : "Notification"}
                            color={isAlert ? "error" : isTask ? "warning" : isSms ? "secondary" : "primary"}
                            variant="outlined"
                          />
                          {!msg.read ? <Chip size="small" label="Unread" color="warning" /> : <Chip size="small" label="Read" variant="outlined" />}
                        </Stack>

                        <Typography variant="subtitle2" sx={{ fontWeight: 900, mt: 0.6 }}>
                          {msg.title || "Notification"}
                        </Typography>

                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.6, whiteSpace: "pre-wrap" }}>
                          {msg.message}
                        </Typography>

                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                          <AccessTimeIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                          <Typography variant="caption" color="text.secondary">
                            {new Date(msg.createdAt).toLocaleString("en-IN")}
                          </Typography>
                        </Stack>
                      </Box>
                    </Stack>

                    {/* Actions */}
                    <Stack direction="row" spacing={1} alignItems="center">
                      {!msg.read && (
                        <Tooltip title="Mark as read">
                          <IconButton onClick={() => markAsRead(msg._id)} color="success">
                            <CheckCircleIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Delete">
                        <IconButton onClick={() => askDelete(msg._id)} color="error">
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          )}
        </Box>

        <Divider />

        {/* Pagination */}
        <Box sx={{ p: 2, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          <Typography variant="body2" color="text.secondary">
            Showing {pagedMessages.length} of {filteredMessages.length} items
          </Typography>

          <Pagination
            count={totalPagesResolved}
            page={page}
            onChange={(_, v) => setPage(v)}
            color="primary"
            shape="rounded"
          />
        </Box>
      </Paper>

      {/* Confirm Delete */}
      <Dialog open={confirm.open} onClose={() => setConfirm({ open: false, id: null })}>
        <DialogTitle>Delete notification?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm({ open: false, id: null })} variant="outlined">
            Cancel
          </Button>
          <Button onClick={deleteMessage} variant="contained" color="error" startIcon={<DeleteIcon />}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4500}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.severity}
          variant="filled"
          sx={{ borderRadius: 2 }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
